import { Component, OnInit, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';
import { DomSanitizer } from '@angular/platform-browser';
import { IPatient } from '@commons/interfaces';
import { StateService } from '@services/state.service';

// Servicios
import { AppointmentService } from '@services/appointments.service';
import { PatientService } from '@services/patient.service';
import { LabResultsService, ILabResultResponse } from '@services/lab-results.service';

// Componentes hijos
import { ListaParaclinicosComponent } from './components/lista-paraclinicos/lista-paraclinicos.component';
import { DetalleParaclinicoComponent } from './components/detalle-paraclinico/detalle-paraclinico.component';
import { PerfilPacienteComponent } from '../../../../perfil-paciente/perfil-paciente.component';

// Interfaces
import {
  IParaclinico,
  ModoVista,
  EstadoParaclinicosPage,
  FiltrosParaclinicos,
  labResultToParaclinico,
} from './interfaces/paraclinico.interface';

@Component({
  selector: 'app-paraclinicos-page',
  standalone: true,
  imports: [CommonModule, ListaParaclinicosComponent, DetalleParaclinicoComponent, PerfilPacienteComponent],
  templateUrl: './paraclinicos-page.component.html',
  styleUrls: ['./paraclinicos-page.component.scss'],
})
export class ParaclinicosPageComponent implements OnInit {
  // IDs desde la URL
  appointmentId: number | null = null;
  patientId: number | null = null;
  returnTo: string | null = null;

  // Estado global
  estado: EstadoParaclinicosPage = {
    paraclinicoSeleccionado: null,
    modoVista: 'ver',
    filtros: { tipo: 'todos' },
    cargando: false,
    error: null,
  };

  // Lista completa de paraclínicos
  paraclinicos: IParaclinico[] = [];

  // Datos del paciente
  paciente: IPatient | null = null;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private apptService: AppointmentService,
    private patientService: PatientService,
    private labResultsService: LabResultsService,
    private sanitizer: DomSanitizer,
    private cdr: ChangeDetectorRef,
    private stateService: StateService,
  ) {}

  async ngOnInit(): Promise<void> {
    // Leer returnTo del navigation state (no aparece en URL)
    this.returnTo = history.state?.returnTo || null;
    console.log('[ParaclinicosPage] returnTo (desde state):', this.returnTo);

    this.route.params.subscribe(async (params) => {
      this.patientId = params['patientId'] ? parseInt(params['patientId']) : null;
      console.log('[ParaclinicosPage] patientId:', this.patientId);

      if (this.patientId) {
        await this.loadPaciente();
        await this.loadParaclinicosFromBackend();
      }
    });

    this.route.queryParams.subscribe(async (params) => {
      this.appointmentId = params['appointmentId'] ? parseInt(params['appointmentId']) : null;
      console.log('[ParaclinicosPage] appointmentId:', this.appointmentId);
    });
  }

  // ==================== CARGAR DATOS ====================
  private async loadParaclinicosFromBackend(): Promise<void> {
    if (!this.patientId) return;

    this.estado.cargando = true;
    this.estado.error = null;

    try {
      console.log('[ParaclinicosPage] Cargando paraclínicos del paciente:', this.patientId);
      const labResults = await this.labResultsService.getByPatient(this.patientId);
      console.log('[ParaclinicosPage] Lab results obtenidos del backend:', labResults.length);
      console.log('[ParaclinicosPage] DEBUG - Primer lab result:', JSON.stringify(labResults[0], null, 2));

      const deduplicados = this.deduplicarParaclinicos(labResults);
      console.log('[ParaclinicosPage] Lab results después de deduplicar:', deduplicados.length);

      this.paraclinicos = deduplicados.map((labResult) => {
        const paraclinico = labResultToParaclinico(labResult);
        console.log('[ParaclinicosPage] DEBUG - pdfUrl generado:', paraclinico.pdfUrl);
        return {
          ...paraclinico,
          interpretacionSafe: this.sanitizer.bypassSecurityTrustHtml(paraclinico.interpretacion || ''),
          pdfUrlSafe: paraclinico.pdfUrl ? this.sanitizer.bypassSecurityTrustResourceUrl(paraclinico.pdfUrl) : undefined,
        } as IParaclinico;
      });

      console.log('[ParaclinicosPage] Paraclínicos cargados:', this.paraclinicos.length);
      this.cdr.detectChanges();
    } catch (error) {
      console.error('[ParaclinicosPage] Error cargando paraclínicos:', error);
      this.estado.error = 'No se pudieron cargar los paraclínicos';
      this.paraclinicos = [];
    } finally {
      this.estado.cargando = false;
      this.cdr.detectChanges();
    }
  }

  /**
   * Deduplica los paraclínicos basándose en el archivo PDF
   * Si hay múltiples registros con el mismo pdf_url, conserva solo el más reciente
   */
  private deduplicarParaclinicos(labResults: ILabResultResponse[]): ILabResultResponse[] {
    const mapaUnicos = new Map<string, ILabResultResponse>();

    for (const labResult of labResults) {
      const clave = this.obtenerClaveDeduplicacion(labResult);

      if (!mapaUnicos.has(clave)) {
        mapaUnicos.set(clave, labResult);
      } else {
        const existente = mapaUnicos.get(clave)!;
        if (new Date(labResult.created_at) > new Date(existente.created_at)) {
          mapaUnicos.set(clave, labResult);
        }
      }
    }

    return Array.from(mapaUnicos.values()).sort((a, b) => {
      return new Date(b.fecha).getTime() - new Date(a.fecha).getTime();
    });
  }

  /**
   * Obtiene una clave única para identificar duplicados
   * Usa el ID del lab_result como clave principal (más confiable)
   * Fallback: file_url o combinación de nombre+fecha+patient_id
   */
  private obtenerClaveDeduplicacion(labResult: ILabResultResponse): string {
    // Usar ID como clave principal - cada registro tiene un ID único
    if (labResult.id) {
      return `id_${labResult.id}`;
    }

    // Fallback: usar file_url si existe
    if (labResult.file_url) {
      return labResult.file_url;
    }

    // Último recurso: usar campos en español (como los devuelve el backend)
    const nombre = labResult.nombre || labResult.test_name || 'sin_nombre';
    const fecha = labResult.fecha || labResult.test_date || 'sin_fecha';
    return `${nombre}_${fecha}_${labResult.patient_id}`;
  }

  private async loadPaciente(): Promise<void> {
    if (!this.patientId) {
      console.log('[ParaclinicosPage] No hay patientId, no se puede cargar paciente');
      return;
    }

    console.log('[ParaclinicosPage] Cargando paciente con ID:', this.patientId);

    try {
      const response = await this.patientService.getPatient(this.patientId);
      this.paciente = response.data;
      console.log('[ParaclinicosPage] Paciente cargado exitosamente:', this.paciente);
      console.log('[ParaclinicosPage] ¿Paciente tiene datos?', !!this.paciente);
      console.log('[ParaclinicosPage] Datos del paciente:', JSON.stringify(this.paciente, null, 2));

      // ← AGREGAR ESTAS 2 LÍNEAS
      // Setear el paciente en StateService para que perfil-paciente lo tome
      this.stateService.setCurrentPatient(this.paciente);
      console.log('[ParaclinicosPage] Paciente seteado en StateService');

      // FORZAR detección de cambios
      this.cdr.detectChanges();

      // Verificar después de forzar cambios
      setTimeout(() => {
        console.log('[ParaclinicosPage] Después de detectChanges, paciente:', !!this.paciente);
      }, 100);
    } catch (error) {
      console.error('[ParaclinicosPage] Error cargando paciente:', error);
      this.estado.error = 'No se pudo cargar la información del paciente';
    }
  }

  // ==================== EVENTOS DE LISTA (IZQUIERDA) ====================
  onParaclinicoSeleccionado(paraclinico: IParaclinico): void {
    console.log('[ParaclinicosPage] Paraclínico seleccionado:', paraclinico);
    this.estado.paraclinicoSeleccionado = paraclinico;
    this.estado.modoVista = 'ver';
  }

  onNuevoParaclinico(): void {
    console.log('[ParaclinicosPage] Crear nuevo paraclínico');
    this.estado.paraclinicoSeleccionado = null;
    this.estado.modoVista = 'cargar';
  }

  onFiltrosChange(filtros: FiltrosParaclinicos): void {
    console.log('[ParaclinicosPage] Filtros cambiados:', filtros);
    this.estado.filtros = filtros;
  }

  // ==================== EVENTOS DE DETALLE (CENTRO) ====================
  async onParaclinicoGuardado(paraclinico: IParaclinico): Promise<void> {
    console.log('[ParaclinicosPage] Paraclínico guardado:', paraclinico);

    await this.loadParaclinicosFromBackend();

    const nuevoParaclinico = this.paraclinicos.find((p) => p.id === paraclinico.id);
    if (nuevoParaclinico) {
      this.estado.paraclinicoSeleccionado = nuevoParaclinico;
      this.estado.modoVista = 'ver';
    }
  }

  async onParaclinicoActualizado(paraclinico: IParaclinico): Promise<void> {
    console.log('[ParaclinicosPage] Paraclínico actualizado:', paraclinico);

    await this.loadParaclinicosFromBackend();

    const actualizado = this.paraclinicos.find((p) => p.id === paraclinico.id);
    if (actualizado) {
      this.estado.paraclinicoSeleccionado = actualizado;
    }
  }

  onCancelarEdicion(): void {
    console.log('[ParaclinicosPage] Cancelar edición');
    this.estado.modoVista = 'ver';
    this.estado.paraclinicoSeleccionado = null;
  }

  onVolver(): void {
    console.log('[ParaclinicosPage] Volver, returnTo:', this.returnTo);

    if (this.returnTo && this.patientId) {
      const ruta = `/expediente/expediente-digital/${this.returnTo}`;
      console.log('[ParaclinicosPage] Regresando a nota:', ruta);
      this.router.navigate([ruta], {
        queryParams: {
          patientId: this.patientId,
          appointmentId: this.appointmentId
        }
      });
    } else {
      console.log('[ParaclinicosPage] Regresando a expediente completo');
      this.router.navigate(['/expediente/expediente-completo']);
    }
  }

  // ==================== GETTERS ====================
  get paraclinicosFiltrados(): IParaclinico[] {
    let filtrados = [...this.paraclinicos];

    // Filtrar por tipo
    if (this.estado.filtros.tipo && this.estado.filtros.tipo !== 'todos') {
      filtrados = filtrados.filter((p) => p.tipo === this.estado.filtros.tipo);
    }

    // Filtrar por búsqueda
    if (this.estado.filtros.busqueda) {
      const busqueda = this.estado.filtros.busqueda.toLowerCase();
      filtrados = filtrados.filter(
        (p) => p.nombre?.toLowerCase().includes(busqueda) || p.interpretacion?.toLowerCase().includes(busqueda),
      );
    }

    // Filtrar por rango de fechas
    if (this.estado.filtros.fechaDesde) {
      filtrados = filtrados.filter((p) => p.fecha >= this.estado.filtros.fechaDesde!);
    }

    if (this.estado.filtros.fechaHasta) {
      filtrados = filtrados.filter((p) => p.fecha <= this.estado.filtros.fechaHasta!);
    }

    return filtrados;
  }
}
